/**
 * PhotoCapture.tsx
 * Camera capture input with client-side downscale to ~1600px JPEG @0.8
 * via canvas, then upload via presigned S3 URL.
 *
 * Props:
 *   label       - 'fuel' | 'expense' | 'tach' — used in the suggested filename
 *   date        - YYYY-MM-DD string — used in the suggested filename
 *   partnerId   - partner ID string — used in the suggested filename
 *   buttonLabel - optional text for the camera button; defaults to receipt
 *                 wording. Pass a tach-specific label when used outside the
 *                 fuel/expense receipt flow.
 *   onUploadComplete(key) - called with the S3 object key after upload
 *   onUploadError(msg)    - called if upload fails (optional; non-fatal)
 */

import React, { useRef, useState } from 'react'
import { getReceiptUploadUrl, uploadReceipt } from '../api'

interface PhotoCaptureProps {
  label: string;
  date: string;
  partnerId: string;
  buttonLabel?: string;
  onUploadComplete: (key: string) => void;
  onUploadError?: (message: string) => void;
}

const MAX_PX = 1600
const JPEG_QUALITY = 0.8

/** Downscale an image to fit within MAX_PX on the longest side, return a Blob. */
function downscaleImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read file'))
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string
      const img = new Image()
      img.onerror = () => reject(new Error('Could not decode image'))
      img.onload = () => {
        let w = img.naturalWidth
        let h = img.naturalHeight

        if (w > MAX_PX || h > MAX_PX) {
          if (w >= h) {
            h = Math.round((h * MAX_PX) / w)
            w = MAX_PX
          } else {
            w = Math.round((w * MAX_PX) / h)
            h = MAX_PX
          }
        }

        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context unavailable'))
        ctx.drawImage(img, 0, 0, w, h)
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Canvas toBlob returned null'))
          },
          'image/jpeg',
          JPEG_QUALITY,
        )
      }
      img.src = dataUrl
    }
    reader.readAsDataURL(file)
  })
}

type UploadState = 'idle' | 'processing' | 'uploading' | 'done' | 'error'

export default function PhotoCapture({
  label,
  date,
  partnerId,
  buttonLabel,
  onUploadComplete,
  onUploadError,
}: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [statusMsg, setStatusMsg] = useState('')

  const inputId = `photo-capture-${label}`
  const busy = uploadState === 'processing' || uploadState === 'uploading'

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFileName(file.name)
    setUploadState('processing')
    setStatusMsg('Processing photo…')
    setPreviewUrl(null)

    try {
      const blob = await downscaleImage(file)

      const previewDataUrl = URL.createObjectURL(blob)
      setPreviewUrl(previewDataUrl)

      setUploadState('uploading')
      setStatusMsg('Uploading receipt…')

      const safeDate = date.replace(/[^0-9-]/g, '') || 'unknown'
      const safePartner = partnerId.replace(/[^A-Za-z0-9]/g, '') || 'unknown'
      const filename = `${label}_${safeDate}_${safePartner}.jpg`

      const { uploadUrl, key } = await getReceiptUploadUrl(filename, 'image/jpeg')
      await uploadReceipt(uploadUrl, blob)

      setUploadState('done')
      setStatusMsg('Receipt uploaded.')
      onUploadComplete(key)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed.'
      setUploadState('error')
      setStatusMsg(msg)
      onUploadError?.(msg)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function handleRemove() {
    setSelectedFileName(null)
    setPreviewUrl(null)
    setUploadState('idle')
    setStatusMsg('')
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        disabled={busy}
        style={{ display: 'none' }}
      />
      {uploadState !== 'done' ? (
        <label
          htmlFor={inputId}
          className={`photo-capture-btn${busy ? ' photo-capture-btn--busy' : ''}`}
        >
          {buttonLabel ?? '📷 Tap to take photo or upload receipt'}
        </label>
      ) : (
        <div className="photo-capture-chosen">
          <span className="photo-capture-filename">{selectedFileName}</span>
          <button type="button" className="photo-capture-remove" onClick={handleRemove}>
            Remove
          </button>
        </div>
      )}
      {statusMsg && (
        <p
          className="field-note"
          style={{ color: uploadState === 'error' ? '#c5221f' : '#137333' }}
        >
          {statusMsg}
        </p>
      )}
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Receipt preview"
          className="photo-preview"
        />
      )}
    </div>
  )
}
